import {useHotkeys} from 'react-hotkeys-hook';
import {useDispatch, useSelector} from 'react-redux';
import {ActionCreators} from 'redux-undo';
import {SlideElementHotkeys} from '../constants/slideElementHotkeys';
import {hasValidClipboardDataSelector} from '../store/selectors/clipboard.selectors';
import {highlightedElementIndexSelector} from '../store/selectors/slide.selectors';
import useElementActions from '../utils/hooks/slideUpdates/useElementActions';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {setSidePanelContentAction} from '../store/actions/grid';
import {SidePanelContent} from '../store/actions/types/grid.types';

export function useHotkeysSlideElementKeys(addElementHandler: (type: ELEMENT) => void): void {
    const hasValidClipboardData = useSelector(hasValidClipboardDataSelector);
    const elementIndex = useSelector(highlightedElementIndexSelector);

    const {duplicateElement, exportElement, deleteElement, copyElement, pasteElement, cutElement} = useElementActions();

    const dispatch = useDispatch();

    useHotkeys(SlideElementHotkeys.Duplicate.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        duplicateElement(elementIndex);
    });

    useHotkeys(SlideElementHotkeys.Delete.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        deleteElement(elementIndex);
    });

    useHotkeys(SlideElementHotkeys.Cut.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        cutElement(elementIndex);
    });

    useHotkeys(SlideElementHotkeys.Copy.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        if (elementIndex >= 0) {
            copyElement(elementIndex);
        }
    });

    useHotkeys(SlideElementHotkeys.Paste.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        if (hasValidClipboardData) {
            pasteElement();
        }
    });

    useHotkeys(SlideElementHotkeys.Export.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        exportElement(elementIndex);
    });

    useHotkeys(SlideElementHotkeys.Undo.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        dispatch(ActionCreators.undo());
    });

    useHotkeys(SlideElementHotkeys.Redo.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        dispatch(ActionCreators.redo());
    });

    useHotkeys(SlideElementHotkeys.ElementText.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.STATIC_TEXT);
    });

    useHotkeys(SlideElementHotkeys.ElementQuestion.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.QUESTION);
    });

    useHotkeys(SlideElementHotkeys.ElementYesNo.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.YES_NO_QUESTION_V2);
    });

    useHotkeys(SlideElementHotkeys.ElementMultiselect.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.MULTI_SELECT);
    });

    useHotkeys(SlideElementHotkeys.ElementLine.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.LINE_V2);
    });

    useHotkeys(SlideElementHotkeys.SidepanelSlideSettings.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        dispatch(setSidePanelContentAction(SidePanelContent.SLIDE));
    });

    useHotkeys(SlideElementHotkeys.ElementTable.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.STATIC_TABLE_V2);
    });

    useHotkeys(SlideElementHotkeys.ElementPhoto.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.PHOTO_ANSWER_QUESTION);
    });

    useHotkeys(SlideElementHotkeys.ElementImageMarkup.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.IMAGE_MARKUP);
    });

    useHotkeys(SlideElementHotkeys.ElementSequence.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.SEQUENCE_QUESTION);
    });

    useHotkeys(SlideElementHotkeys.ElementMatchPair.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.MATCH_PAIR_QUESTION);
    });

    useHotkeys(SlideElementHotkeys.ElementLikertScale.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.LIKERT_SCALE);
    });

    useHotkeys(SlideElementHotkeys.ElementArrow.keys, (keyboardEvent) => {
        keyboardEvent.preventDefault();
        addElementHandler(ELEMENT.ARROW_V2);
    });
}
